import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';

import { passwordMeetsPolicy } from './passwordPolicy';

/**
 * Reglas y utilidades del Form A que no son pintado.
 *
 * Viven fuera del componente para que la validacion se pueda leer de un vistazo junto a su
 * gemela de servidor (`src/Listener/SaveUmbralProfile.php`): el cliente valida por UX —
 * M2b pide error inline al enviar, con foco al primero— y el servidor valida porque es el
 * unico que manda. Cuando las dos discrepan, gana el servidor y el formulario lo muestra
 * igual, campo a campo.
 */

/** Campos del formulario, en el orden en que aparecen (el foco al primer error lo usa). */
export const FIELD_ORDER = [
  'first_name',
  'last_name',
  'email',
  'alt_email',
  'username',
  'company',
  'role',
  'source',
  'topics',
  'experience',
  'coding',
  'password',
  'password_confirm',
  'terms',
] as const;

export type FieldName = (typeof FIELD_ORDER)[number];
export type Errors = Partial<Record<FieldName, string>>;

/**
 * Catalogo de opciones tal como lo publica el servidor
 * (`Umbral\Comunidad\Api\AddProfileOptions`). Si por lo que sea no llegara, el formulario
 * no se inventa listas: se queda vacio y se ve, en vez de ofrecer opciones que el validador
 * rechazaria.
 */
export type ProfileOptions = {
  role: string[];
  source: string[];
  topics: string[];
  experience: string[];
  coding: string[];
};

export function profileOptions(): ProfileOptions {
  const raw = app.forum.attribute<Partial<ProfileOptions>>('umbralComunidadProfileOptions') || {};

  return {
    role: raw.role || [],
    source: raw.source || [],
    topics: raw.topics || [],
    experience: raw.experience || [],
    coding: raw.coding || [],
  };
}

/**
 * ¿El servidor sigue exigiendo un codigo de invitacion para darse de alta?
 *
 * DECISION DE PRODUCTO T11 (D): el alta de la Comunidad Umbral es PUBLICA y el Form A no pide
 * codigo nunca. El codigo (`fof/doorman`) queda solo para las vias de n8n Chile.
 *
 * Esta sonda ya NO decide si se pinta un campo — decide si se puede pintar el FORMULARIO. Si
 * un dia `fof-doorman.allowPublic` volviera a `false` (por un ajuste revertido, o porque el
 * deploy aplico el codigo antes que el ajuste), enviar el Form A daria un 422 de doorman sobre
 * un campo que ya no existe. Antes que eso, `RegistroPage` muestra el estado «solo por
 * invitacion» y explica que hacer. Volver a meter el campo NO es una opcion: lo prohibe el
 * paquete.
 *
 * `fof/doorman` serializa siempre `fof-doorman.allowPublic` cuando esta habilitado (con
 * `false` por defecto), asi que su AUSENCIA significa «la extension no esta» — y entonces no
 * hay nada que exigir. Distinguir los dos casos importa: sin la distincion, un foro sin
 * doorman nunca podria dar de alta a nadie.
 */
export function signupNeedsInvite(): boolean {
  const allowPublic = app.forum.attribute('fof-doorman.allowPublic');

  return allowPublic !== undefined && allowPublic !== null && !allowPublic;
}

/**
 * Nombre de usuario sugerido a partir de nombre y apellido.
 *
 * El mockup M2 no pide usuario, pero Flarum lo exige, unico y con
 * `regex:/^[a-z0-9_-]+$/i` + `min:3` + `max:30` (core `UserValidator`). Generarlo en
 * silencio seria una trampa: al chocar con uno existente el 422 apuntaria a un campo que la
 * persona no ve. Por eso se sugiere y se deja editable — visible, corregible y con la regla
 * explicada al lado.
 */
export function suggestUsername(first: string, last: string): string {
  const base = `${first} ${last}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // acentos fuera: la regex del core no los admite
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return base.slice(0, 30);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-z0-9_-]+$/i;

/** Prefijo de los mensajes de error; lo comparten la validacion de cliente y el mapeo del 422. */
const ERRORS = 'umbral-comunidad.forum.registro.errors';

export type FormValues = {
  first_name: string;
  last_name: string;
  email: string;
  alt_email: string;
  username: string;
  company: string;
  role: string;
  source: string;
  topics: string[];
  experience: string;
  coding: string;
  password: string;
  password_confirm: string;
  terms: boolean;
};

/**
 * Validacion de cliente. Espeja la del servidor y anade lo que solo el cliente puede saber
 * (que las dos contrasenas coinciden: el servidor solo recibe una).
 */
export function validate(values: FormValues): Errors {
  const t = (key: string) => extract(`${ERRORS}.${key}`);
  const errors: Errors = {};

  if (!values.first_name.trim()) errors.first_name = t('first_name');
  if (!values.last_name.trim()) errors.last_name = t('last_name');

  if (!values.email.trim()) errors.email = t('email_required');
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = t('email_invalid');

  if (values.alt_email.trim() && !EMAIL_RE.test(values.alt_email.trim())) errors.alt_email = t('alt_email');

  const username = values.username.trim();
  if (!username) errors.username = t('username_required');
  else if (!USERNAME_RE.test(username) || username.length < 3 || username.length > 30) errors.username = t('username_invalid');

  if (!values.company.trim()) errors.company = t('company');
  if (!values.role) errors.role = t('role');
  if (!values.source) errors.source = t('source');
  if (!values.topics.length) errors.topics = t('topics');
  if (!values.experience) errors.experience = t('experience');
  if (!values.coding) errors.coding = t('coding');

  // La misma politica global que aplica el listener del servidor. Las reglas viven en
  // `utils/passwordPolicy` desde PKG-FORO-PASSWORD-SETTINGS-UX: tambien las pinta el modal
  // de Configuracion, y dos copias en el mismo bundle acabarian divergiendo.
  if (!values.password) errors.password = t('password_required');
  else if (!passwordMeetsPolicy(values.password)) errors.password = t('password_policy');

  if (values.password_confirm !== values.password) errors.password_confirm = t('password_confirm');

  if (!values.terms) errors.terms = t('terms');

  return errors;
}

/**
 * Punteros JSON:API -> campos del formulario.
 *
 * El core devuelve `/data/attributes/username` y nuestro listener
 * `/data/attributes/umbralProfile.<campo>`. Lo que no se reconozca se devuelve aparte para
 * mostrarlo entero en la barra de error: perder un mensaje del servidor porque no supimos
 * donde ponerlo seria peor que ensenarlo suelto.
 *
 * T11 (D): `fof-doorkey` YA NO se ancla a ningun campo — el Form A no lo pide. Si doorman
 * respondiera igualmente (ver `signupNeedsInvite`), su mensaje cae en `unmapped` y sale entero
 * en la barra de arriba. Anclarlo a un campo inexistente si que lo haria desaparecer: el
 * componente solo pinta mensajes dentro de un `[data-cm-field]` que exista.
 */
export function mapServerErrors(response: any): { errors: Errors; unmapped: string[] } {
  const errors: Errors = {};
  const unmapped: string[] = [];
  const list: any[] = (response && response.errors) || [];

  const known = new Set<string>([...FIELD_ORDER]);

  list.forEach((error) => {
    const pointer: string = (error && error.source && error.source.pointer) || '';
    const detail: string = (error && error.detail) || '';
    const raw = pointer.replace('/data/attributes/', '');

    let field: string | null = null;

    if (raw.indexOf('umbralProfile.') === 0) field = raw.slice('umbralProfile.'.length).split('.')[0];
    else if (known.has(raw)) field = raw;

    // Nunca se guarda una cadena vacia: un error sin `detail` contaria en la barra («revisa
    // 1 campo») pero no pintaria ni mensaje ni borde rojo — la persona buscaria un campo que
    // no esta marcado. Con detalle o sin el, siempre hay algo escrito junto al campo.
    if (field && known.has(field)) errors[field as FieldName] = detail || errors[field as FieldName] || extract(`${ERRORS}.generic`);
    else if (detail) unmapped.push(detail);
  });

  return { errors, unmapped };
}

/**
 * `app.translator.trans` devuelve vnodes en cuanto la traduccion lleva marcado; los mensajes
 * de error se guardan como cadena (van dentro de un `<p>` y se comparan en las specs).
 */
function extract(key: string): string {
  return extractText(app.translator.trans(key));
}
